/**
 * waap-aiwaf-settings-tab.tsx — AI-WAF Settings tab (B3 batch).
 *
 * Four account-level settings aspects — each a separate section with its own
 * Save button routing through ActAsMutationConfirm (reason required):
 *   1. WAF Mode (protection level 1-9)
 *   2. IP Block List (global blacklist textarea)
 *   3. Bot Policy (rule IDs toggle list)
 *   4. Account Rate Limit (enabled + rate + window + action)
 *
 * Data sources (all anchor-scoped):
 *   GET/PUT /waap/domains/:anchorId/mode
 *   GET/PUT /waap/domains/:anchorId/ip-lists
 *   GET/PUT /waap/domains/:anchorId/bot-policy
 *   GET/PUT /waap/domains/:anchorId/rate-limit
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Switch, Textarea,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { Section, SettingRow } from '@vnetwork/ui/layout';
import { TabError } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import {
  useActAsWAAPMode,
  useActAsWAAPIPLists,
  useActAsWAAPBotPolicy,
  useActAsWAAPAccountRateLimit,
  type WaapRateLimit,
} from '@/api/act-as-waap-account';
import { NumericInput } from '@/components/numeric-input';

/* ── Pending mutation discriminator ──────────────────────────────────────── */

type PendingKind = 'mode' | 'ip-lists' | 'bot-policy' | 'rate-limit';

interface PendingMutation {
  kind: PendingKind;
  payload: unknown;
}

/* ── Section: WAF Mode ───────────────────────────────────────────────────── */

function ModeSection({
  anchorId,
  onMutate,
}: {
  anchorId: string;
  onMutate: (p: PendingMutation) => void;
}) {
  const { t } = useTranslation();
  const modeQ = useActAsWAAPMode(anchorId);
  const [draft, setDraft] = useState('');

  // Sync draft from server on first load.
  useEffect(() => {
    if (modeQ.data?.level != null && draft === '') {
      setDraft(String(modeQ.data.level));
    }
  }, [modeQ.data, draft]);

  const serverLevel = modeQ.data?.level ?? 0;
  const isDirty = draft !== '' && draft !== String(serverLevel);
  const LEVELS = ['1','2','3','4','5','6','7','8','9'];

  if (modeQ.isError) {
    return (
      <TabError
        title={t('waap_domain.settings_mode_error', 'Failed to load protection mode')}
        onRetry={() => modeQ.refetch()}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('waap_domain.settings_mode_title', 'WAF Mode')}</CardTitle>
        <CardDescription>
          {t('waap_domain.settings_mode_desc', 'Account-wide protection level (1 = off, 9 = most strict).')}
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {modeQ.isLoading ? (
          <Skeleton style={{ width: 140, height: 36 }} />
        ) : (
          <Field label={t('waap_domain.settings_mode_level', 'Level')} style={{ minWidth: 140 }}>
            {(props) => (
              <Select value={draft || String(serverLevel)} onValueChange={setDraft}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        )}
        <Button
          size="sm"
          variant="primary"
          disabled={!isDirty || modeQ.isLoading}
          onClick={() => onMutate({ kind: 'mode', payload: { level: parseInt(draft, 10) } })}
        >
          {t('waap_domain.settings_save', 'Save')}
        </Button>
      </CardBody>
    </Card>
  );
}

/* ── Section: IP Block List ──────────────────────────────────────────────── */

function IPListsSection({
  anchorId,
  onMutate,
}: {
  anchorId: string;
  onMutate: (p: PendingMutation) => void;
}) {
  const { t } = useTranslation();
  const ipQ = useActAsWAAPIPLists(anchorId);
  const [draft, setDraft] = useState<string | null>(null);

  const serverList = ipQ.data?.blacklist ?? [];
  const serverText = serverList.join('\n');

  // Draft: null = not yet touched; on first edit clone server value.
  const displayText = draft ?? serverText;
  const isDirty = draft !== null && draft !== serverText;

  function parseIPs(text: string): string[] {
    return text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  }

  if (ipQ.isError) {
    return (
      <TabError
        title={t('waap_domain.settings_iplist_error', 'Failed to load IP block list')}
        onRetry={() => ipQ.refetch()}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('waap_domain.settings_iplist_title', 'IP Block List')}</CardTitle>
        <CardDescription>
          {t(
            'waap_domain.settings_iplist_desc',
            'One IP or CIDR per line. Applied account-wide as a global blacklist.',
          )}
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {ipQ.isLoading ? (
          <Skeleton style={{ width: '100%', height: 100 }} />
        ) : (
          <Field label={t('waap_domain.settings_iplist_label', 'Blocked IPs / CIDRs')}>
            {(props) => (
              <Textarea
                {...props}
                value={displayText}
                rows={6}
                placeholder={'192.168.1.0/24\n10.0.0.1'}
                onChange={(e) => setDraft(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}
              />
            )}
          </Field>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="sm"
            variant="primary"
            disabled={!isDirty || ipQ.isLoading}
            onClick={() =>
              onMutate({
                kind: 'ip-lists',
                payload: { blacklist: parseIPs(draft ?? '') },
              })
            }
          >
            {t('waap_domain.settings_save', 'Save')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Section: Bot Policy ─────────────────────────────────────────────────── */

// Known bot rule IDs from Partner source (display names inferred from vendor docs).
const KNOWN_BOT_RULES: { id: number; label: string }[] = [
  { id: 1,  label: 'Search Engine Crawlers' },
  { id: 2,  label: 'Social Media Bots' },
  { id: 3,  label: 'Monitoring & Analytics' },
  { id: 4,  label: 'Advertising & Marketing' },
  { id: 5,  label: 'Feed Fetchers' },
  { id: 6,  label: 'Cloud Providers' },
  { id: 7,  label: 'Data Aggregators' },
  { id: 8,  label: 'Security Scanners' },
];

function BotPolicySection({
  anchorId,
  onMutate,
}: {
  anchorId: string;
  onMutate: (p: PendingMutation) => void;
}) {
  const { t } = useTranslation();
  const botQ = useActAsWAAPBotPolicy(anchorId);
  const [draftIds, setDraftIds] = useState<number[] | null>(null);

  const serverIds = botQ.data?.rule_ids ?? [];
  const effectiveIds = draftIds ?? serverIds;

  const isDirty =
    draftIds !== null &&
    JSON.stringify([...draftIds].sort()) !== JSON.stringify([...serverIds].sort());

  function toggle(id: number) {
    const current = effectiveIds;
    setDraftIds(
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );
  }

  if (botQ.isError) {
    return (
      <TabError
        title={t('waap_domain.settings_bot_error', 'Failed to load bot policy')}
        onRetry={() => botQ.refetch()}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('waap_domain.settings_bot_title', 'Bot Policy')}</CardTitle>
        <CardDescription>
          {t(
            'waap_domain.settings_bot_desc',
            'Select trusted bot categories to exempt from WAF rules account-wide.',
          )}
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {botQ.isLoading ? (
          <Skeleton style={{ width: '100%', height: 120 }} />
        ) : (
          <Section>
            {KNOWN_BOT_RULES.map((rule) => (
              <SettingRow
                key={rule.id}
                title={rule.label}
                control={
                  <Switch
                    checked={effectiveIds.includes(rule.id)}
                    onCheckedChange={() => toggle(rule.id)}
                  />
                }
              />
            ))}
          </Section>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="sm"
            variant="primary"
            disabled={!isDirty || botQ.isLoading}
            onClick={() =>
              onMutate({ kind: 'bot-policy', payload: { rule_ids: effectiveIds } })
            }
          >
            {t('waap_domain.settings_save', 'Save')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Section: Account Rate Limit ─────────────────────────────────────────── */

function RateLimitSection({
  anchorId,
  onMutate,
}: {
  anchorId: string;
  onMutate: (p: PendingMutation) => void;
}) {
  const { t } = useTranslation();
  const rlQ = useActAsWAAPAccountRateLimit(anchorId);
  const [draft, setDraft] = useState<WaapRateLimit | null>(null);

  const server: WaapRateLimit = rlQ.data ?? {};
  const form: WaapRateLimit = draft ?? server;

  const isDirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(server);

  function patch(key: string, value: unknown) {
    setDraft((prev) => ({ ...(prev ?? server), [key]: value }));
  }

  const ACTIONS = ['block', 'challenge', 'throttle'];

  if (rlQ.isError) {
    return (
      <TabError
        title={t('waap_domain.settings_rl_error', 'Failed to load rate limit config')}
        onRetry={() => rlQ.refetch()}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('waap_domain.settings_rl_title', 'Account Rate Limit')}</CardTitle>
        <CardDescription>
          {t(
            'waap_domain.settings_rl_desc',
            'Global rate-limiting applied account-wide across all websites.',
          )}
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {rlQ.isLoading ? (
          <Skeleton style={{ width: '100%', height: 100 }} />
        ) : (
          <>
            <SettingRow
              title={t('waap_domain.settings_rl_enabled', 'Enabled')}
              control={
                <Switch
                  checked={Boolean(form.enabled)}
                  onCheckedChange={(v) => patch('enabled', v)}
                />
              }
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 'var(--vn-space-3)',
              }}
            >
              <Field label={t('waap_domain.settings_rl_rate', 'Max requests')}>
                {(props) => (
                  <NumericInput
                    {...props}
                    min={1}
                    value={form.rate != null ? String(form.rate) : ''}
                    placeholder="100"
                    onChange={(e) => patch('rate', parseInt(e.target.value, 10) || 0)}
                  />
                )}
              </Field>
              <Field label={t('waap_domain.settings_rl_window', 'Window (seconds)')}>
                {(props) => (
                  <NumericInput
                    {...props}
                    min={1}
                    value={form.window != null ? String(form.window) : ''}
                    placeholder="60"
                    onChange={(e) => patch('window', parseInt(e.target.value, 10) || 0)}
                  />
                )}
              </Field>
              <Field label={t('waap_domain.settings_rl_action', 'Action')}>
                {(props) => (
                  <Select value={String(form.action ?? 'block')} onValueChange={(v) => patch('action', v)}>
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="sm"
            variant="primary"
            disabled={!isDirty || rlQ.isLoading}
            onClick={() => onMutate({ kind: 'rate-limit', payload: form })}
          >
            {t('waap_domain.settings_save', 'Save')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Root component ──────────────────────────────────────────────────────── */

interface WaapAiwafSettingsTabProps {
  anchorId: string;
}

/**
 * AI-WAF Settings tab — four account-level config sections.
 * Each section saves independently via ActAsMutationConfirm.
 */
export function WaapAiwafSettingsTab({ anchorId }: WaapAiwafSettingsTabProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingMutation | null>(null);

  // Query keys to invalidate per mutation kind.
  const invalidationKeys: Record<PendingKind, readonly (readonly unknown[])[]> = {
    'mode':       [actAsWAAPKeys.aspect(anchorId, 'mode')],
    'ip-lists':   [actAsWAAPKeys.aspect(anchorId, 'ip-lists')],
    'bot-policy': [actAsWAAPKeys.aspect(anchorId, 'bot-policy')],
    'rate-limit': [actAsWAAPKeys.aspect(anchorId, 'rate-limit')],
  };

  const endpointByKind: Record<PendingKind, string> = {
    'mode':       `/waap/domains/${anchorId}/mode`,
    'ip-lists':   `/waap/domains/${anchorId}/ip-lists`,
    'bot-policy': `/waap/domains/${anchorId}/bot-policy`,
    'rate-limit': `/waap/domains/${anchorId}/rate-limit`,
  };

  const titleByKind: Record<PendingKind, string> = {
    'mode':       t('waap_domain.settings_mode_title', 'WAF Mode'),
    'ip-lists':   t('waap_domain.settings_iplist_title', 'IP Block List'),
    'bot-policy': t('waap_domain.settings_bot_title', 'Bot Policy'),
    'rate-limit': t('waap_domain.settings_rl_title', 'Account Rate Limit'),
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      <Alert tone="info" style={{ fontSize: 'var(--vn-font-size-xs)' }}>
        {t(
          'waap_domain.settings_account_note',
          'All settings below apply account-wide across every website in this WAAP service.',
        )}
      </Alert>

      <ModeSection     anchorId={anchorId} onMutate={setPending} />
      <IPListsSection  anchorId={anchorId} onMutate={setPending} />
      <BotPolicySection anchorId={anchorId} onMutate={setPending} />
      <RateLimitSection anchorId={anchorId} onMutate={setPending} />

      <ActAsMutationConfirm
        open={pending !== null}
        onOpenChange={(open) => { if (!open) setPending(null); }}
        title={pending ? titleByKind[pending.kind] : ''}
        summary={
          pending
            ? t(
                'waap_domain.settings_save_summary',
                'Save "{{section}}" settings for this WAAP account.',
                { section: titleByKind[pending.kind] },
              )
            : ''
        }
        queryKeysToInvalidate={pending ? invalidationKeys[pending.kind] : []}
        mutationFn={
          pending
            ? () => actAsClient.put(endpointByKind[pending.kind], pending.payload)
            : async () => {}
        }
        onSuccess={() => setPending(null)}
      />
    </div>
  );
}
